import {
  AlertTriangle,
  ChartNetwork,
  CheckCircle,
  LoaderCircle,
  XCircle,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  FileTextIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  RotateCwIcon,
  TestTubeIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WEBHOOK_PERMISSIONS } from 'shared';
import {
  useDeleteWebhook,
  useTestWebhook,
  useWebhooks,
} from '@/components/features/webhooks/hooks';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Webhook } from '@/lib/api-client';

function WebhooksPage() {
  const { t } = useTranslation('integrations');
  const { data: webhooks, isLoading } = useWebhooks();
  const deleteWebhook = useDeleteWebhook();
  const testWebhook = useTestWebhook();

  const handleDelete = (id: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;
    deleteWebhook.mutate(id);
  };

  const handleTest = (id: string) => {
    testWebhook.mutate(id);
  };

  const getStatusIcon = (status: string) => {
    const iconClass = 'w-4 h-4';
    switch (status) {
      case 'healthy':
        return <HugeiconsIcon icon={CheckCircle} className={`${iconClass} text-green-500`} />;
      case 'degraded':
        return <HugeiconsIcon icon={AlertTriangle} className={`${iconClass} text-yellow-500`} />;
      case 'failing':
        return <HugeiconsIcon icon={XCircle} className={`${iconClass} text-red-500`} />;
      default:
        return <HugeiconsIcon icon={LoaderCircle} className={`${iconClass} text-gray-400`} />;
    }
  };

  const formatLastDelivery = (date: string | null) => {
    if (!date) return 'Never';
    const diff = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const parseEvents = (eventsStr: string) => {
    try {
      return JSON.parse(eventsStr);
    } catch {
      return [];
    }
  };

  // Helper function to type-cast dynamic routes
  const route = (path: string) => path as any;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('webhooks.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('webhooks.description')}</p>
        </div>
        <Link to="/integrations/webhooks/create">
          <Button>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t('webhooks.create')}
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <HugeiconsIcon
            icon={LoaderCircle}
            size={32}
            className="animate-spin text-muted-foreground"
          />
        </div>
      ) : !webhooks || webhooks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <HugeiconsIcon icon={ChartNetwork} size={48} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No webhooks configured yet.</p>
            <Link to="/integrations/webhooks/create">
              <Button variant="outline" className="mt-4">
                Create your first webhook
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {webhooks.map((webhook: Webhook) => (
            <Card key={webhook.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(webhook.lastStatus)}
                    <div>
                      <CardTitle className="text-lg">{webhook.name}</CardTitle>
                      <CardDescription className="truncate max-w-md">{webhook.url}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!webhook.isActive && <Badge variant="secondary">Inactive</Badge>}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVerticalIcon className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={route(`/integrations/webhooks/${webhook.id}`)}>
                            <FileTextIcon className="w-4 h-4 mr-2" />
                            {t('webhooks.viewLogs')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={route(`/integrations/webhooks/${webhook.id}/edit`)}>
                            <PencilIcon className="w-4 h-4 mr-2" />
                            {t('webhooks.edit')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleTest(webhook.id)}
                          disabled={testWebhook.isPending}
                        >
                          <TestTubeIcon className="w-4 h-4 mr-2" />
                          {t('webhooks.test')}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={route(`/integrations/webhooks/${webhook.id}/rotate-secret`)}>
                            <RotateCwIcon className="w-4 h-4 mr-2" />
                            {t('webhooks.rotateSecret')}
                          </Link>
                        </DropdownMenuItem>
                        <PermissionGuard permission={WEBHOOK_PERMISSIONS.DELETE}>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(webhook.id)}
                            disabled={deleteWebhook.isPending}
                          >
                            {t('webhooks.delete')}
                          </DropdownMenuItem>
                        </PermissionGuard>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Events: {parseEvents(webhook.events).length}</span>
                  <span>Success: {webhook.successRate}%</span>
                  <span>Last: {formatLastDelivery(webhook.lastDeliveryAt)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/integrations/webhooks/')({
  component: WebhooksPage,
});
